import './language-switch.scss';

/**
 * Handles the two answers the language switch modal can get.
 *
 * This component listens for the `modal.ready` event (dispatched by the generic
 * Modal component) to receive the modal API for the dialog that contains the
 * language switch markup. Once the API is available, it binds the close CTA to
 * call `closeModal()`.
 *
 * Both answers are recorded server-side through `POST /set-region`: switching
 * writes the other region into the `region` cookie, any dismissal writes the
 * one of the installation the visitor stays on. That cookie is what decides
 * whether the modal is rendered again, so this component keeps no memory of
 * its own.
 */
class LanguageSwitch {
    constructor (element) {
        // Defaults
        this.defaults = {
            initAttr: 'data-languageswitch',
            submitAttr: 'data-languageswitch-url',
            regionAttr: 'data-languageswitch-region',
            closeValue: 'close'
        };

        // Elements
        this.$root = element;
        this.$closeCta = this.$root.querySelector(`[${this.defaults.initAttr}="${this.defaults.closeValue}"]`);
        this.$submitButton = this.$root.querySelector(`[${this.defaults.submitAttr}]`);
        this.$modal = this.$root.closest('dialog');

        // Properties
        this.modalAPI = null;
    }

    /**
     * Waits for the owning modal instance to be initialized and then wires up events.
     *
     * The modal component emits a global `modal.ready` event with an API reference.
     * This method listens for that event, filters it to the dialog that contains
     * this language switch, and stores the API for later use.
     *
     * @returns {void} No return value.
     */
    initialize () {
        const onModalReady = (e) => {
            // Guard against missing event details (e.g., unexpected dispatch shape).
            const detail = e?.detail;
            if (!detail) return;

            // Only react to the modal instance that owns this language switch dialog.
            if (detail.modalTarget === this.$modal) {
                this.modalAPI = detail.api;
                this.modalAPI.openModal();
                this.setEvents();

                // Remove listener once the correct modal API has been captured.
                window.removeEventListener('modal.ready', onModalReady);
            }
        };

        window.addEventListener('modal.ready', onModalReady);
    }

    /**
     * Binds UI event handlers that rely on the modal API being available.
     *
     * @returns {void} No return value.
     */
    setEvents () {
        if (this.$closeCta && this.modalAPI) {
            this.$closeCta.addEventListener('click', () => {
                // Delegate the close behavior to the modal component's API.
                this.modalAPI.closeModal();
            });
        }

        if (this.$submitButton) {
            this.$submitButton.addEventListener('click', () => {
                const targetUrl = this.$submitButton.getAttribute(this.defaults.submitAttr);
                const region = this.$submitButton.getAttribute(this.defaults.regionAttr);

                if (!targetUrl || !region) {
                    return;
                }

                this.$submitButton.disabled = true;

                fetch('/set-region', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: 'region=' + encodeURIComponent(region)
                })
                    .then(response => response.json())
                    .then((data) => {
                        if (data.success) {
                            // Not closed here: `modal.closed` records "stay here" and would overwrite the region just chosen.
                            window.location.href = targetUrl;
                        } else {
                            console.info('Switch not successful');
                            this.$submitButton.disabled = false;
                        }
                    })
                    .catch((error) => {
                        console.error('Error:', error);
                        this.$submitButton.disabled = false;
                    });
            });
        }

        // Every way out of the modal - close CTA, ESC, backdrop - counts as
        // "stay here", so the server stops offering the switch on the next page.
        window.addEventListener('modal.closed', (e) => {
            const $target = e.detail.$context;

            if ($target !== this.$modal) {
                return;
            }

            const region = this.$closeCta?.getAttribute(this.defaults.regionAttr);

            if (!region) {
                return;
            }

            fetch('/set-region', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: 'region=' + encodeURIComponent(region),
                keepalive: true
            }).catch((error) => {
                console.error('Error:', error);
            });
        });
    }
}

export { LanguageSwitch };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-languageswitch="root"]');
        $$roots.forEach(($root) => {
            const $rootAPI = new LanguageSwitch($root);
            $rootAPI.initialize();
        });
    }
});
